import React, { FC } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Typography, Box } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { find } from 'lodash';
import { useStyles } from './styles';
import { OfferingDetailsProps } from './interface';
import EventDetailsDrawer from './event-details-drawer';
import { EventType, ISession } from '../../../../types';
import { ReactComponent as HostSVG } from './assets/profile.svg';
import { ReactComponent as MentorSVG } from './assets/mentor-placeholder.svg';
import SessionDetails from './session-details';

import ExpansionPanel from '../../../shared/expansionPanel';
import OfferingHeaderShrinking from './offering-header-shrinking';
import ChipList from '../../../shared/chip-list';
import useGetMentors from '../../../shared/hook/use-get-mentors';

const hostTitle = (t: TFunction, classes: ClassNameMap) => {
  return (
    <Box className={classes.expansionPanelHeader}>
      <HostSVG />
      <Typography className={classes.expansionPanelTitle}>
        {t('v2.global.label_hostDetails')}
      </Typography>
    </Box>
  );
};

const OfferingDetails: FC<OfferingDetailsProps> = ({
  offering,
  onClose,
  scrolled,
}) => {
  const { t } = useTranslation();
  const offeringType = offering.type;
  const classes = useStyles({ offeringType });
  const { data: mentors } = useGetMentors();
  const isWorkshop = offering.type === EventType.WORKSHOP;

  const mentor = find(mentors, (item) => item.id === offering.mentor?.id);

  const mentorTopicList = mentor?.details?.focusTopics?.map((item) =>
    t(`v2.global.chip_label_topic_${item}`),
  );

  const offeringTopicList = offering.topics.map((topic) =>
    t(`v2.global.chip_label_topic_${topic}`),
  );

  return (
    <Box className={classes.detailsContainer}>
      <Box className={classes.headerContainer}>
        <OfferingHeaderShrinking offering={offering} scrolled={scrolled} />
      </Box>
      <Box className={classes.contentContainer}>
        <Typography variant="h2">{offering.name}</Typography>
        {offering.mentor && (
          <Box className={classes.mentorWrapper}>
            {offering.mentor.profileImage ? (
              <img
                alt="Mentor"
                className={classes.mentorImage}
                src={offering.mentor.profileImage}
              />
            ) : (
              <MentorSVG className={classes.mentorImage} />
            )}

            <Box className={classes.mentorTitleSection}>
              <Typography className={classes.label} variant="body1">
                {offering.mentor.name}
              </Typography>
              <Typography className={classes.yourHostLabel} variant="body1">
                {t('v2.global.label_yourHost')}
              </Typography>
            </Box>
          </Box>
        )}

        {mentor && (
          <Box className={classes.mentorInfoWrapper}>
            <ExpansionPanel
              title={hostTitle(t, classes)}
              content={
                <Box className={classes.expansionPanel}>
                  <Box className={classes.infoMentorWrapper}>
                    <Typography variant="body1">
                      {mentor.name}{' '}
                      {`(${mentor.details?.gender.toLowerCase()})`}
                    </Typography>
                    <Typography className={classes.label} variant="body1">
                      {mentor.details?.education}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      className={classes.hostTopicsLabel}
                      variant="body2"
                    >
                      {t('v2.global.label_specialisedIn')}
                    </Typography>
                    <Box>
                      <ChipList list={mentorTopicList} />
                    </Box>
                    <Typography
                      className={classes.canSupport}
                      variant="subtitle1"
                    >
                      {t('v2.global.label_canSupportOtherTopics')}
                    </Typography>
                  </Box>
                  <Box className={classes.infoMentorWrapper}>
                    <Typography variant="body1">
                      {mentor.details?.description}
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Box>
        )}

        <Box>
          <Typography variant="body2" className={classes.sectionLabel}>
            {t('v2.global.label_details')}
          </Typography>
          <Box>
            <EventDetailsDrawer offering={offering} />
          </Box>
        </Box>

        {!!offeringTopicList?.length && (
          <Box className={classes.infoTopicsWrapper}>
            <Typography variant="body2" className={classes.sectionLabel}>
              {t('v2.global.label_topics')}
            </Typography>

            <ChipList list={offeringTopicList} />
          </Box>
        )}

        {isWorkshop && (
          <Box className={classes.infoWrapper}>
            <Typography variant="body2" className={classes.sectionLabel}>
              {t('v2.global.label_sessions')}
            </Typography>
            {offering.sessions.map((session: ISession) => (
              <SessionDetails session={session} />
            ))}
          </Box>
        )}

        <Box className={classes.infoWrapper}>
          <Typography variant="body2" className={classes.sectionLabel}>
            {t('v2.global.label_moreAboutTheFormat')}
          </Typography>
          <Box
            dangerouslySetInnerHTML={{
              __html: offering.description,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default OfferingDetails;
